import React, { useMemo, useState } from "react";
import dropdownIcon from "../../assets/componentsIcons/dropdown/dropdown.svg";
import editIcon from "../../assets/componentsIcons/dropdown/edit.svg";
import deleteIcon from "../../assets/componentsIcons/dropdown/delete.svg";
import paymentIcon from "../../assets/componentsIcons/dropdown/dollar.svg";
import notesIcon from "../../assets/componentsIcons/dropdown/doc.svg";
import {
  DisplayColorEnum,
  EntityType,
  NotesEntityTypesEnum,
  TenantService,
  TenantVM,
  UnitTypesEnum,
  UnitVM,
} from "@propertelligent/client-api";
import UserIcon from "../../assets/svgs/unitIcons/userIcon";
import LeaseIcon from "../../assets/svgs/unitIcons/leaseIcon";
import VacantIcon from "../../assets/svgs/vacantIcon";
import ArrowOutgoingIcon from "../../assets/svgs/arrows/arrowOutGoingIcon";
import ArrowIncomingIcon from "../../assets/svgs/arrows/arrowIncomingIcon";
import ActionMenu from "../actionMenus/action-menu";
import { ConfirmationPopup } from "../confirmation-popup/confirmation-popup-demo";
import AddNotesForm from "../notes/AddNotesForm";
import { useNavigate } from "react-router-dom";
import { GetImages } from "../../utils/GetImages";
import apiHelper from "../../utils/apiHelper";
import { useLeaseInfoTooltip } from "../../hooks/useLeaseInfoTooltip";
import AddPaymentModal from "../payments/AddPaymentModal";
import CurrencyTooltip from "../../components/currencytooltip/CurrencyTooltip";
import CustomTooltip from "../../components/common/CustomTooltip";
import displayColorMap from "../../utils/displayColorMap";
import LargeTooltip from "../tooltips/large-tooltip";
import BluePayableIcon from "../../assets/Payment-Icon.svg";
import { usePermissions } from "../../hooks/usePermissions";
import { formatUnitEnumLabel } from "../../utils/formatUnitEnumLabel";

interface tenantInfoVM {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: number;
}

interface UnitCardItemProps {
  listingData: UnitVM;
  IsFooter?: boolean;
  showConfirmation?: string | null;
  setShowConfirmation?: (id: string | null) => void;
  handleDeleteUnit?: (unitId: any) => void;
  isDeletingUnit?: boolean;
  onEditUnit?: (unit: UnitVM) => void;
  tenantInfo?: tenantInfoVM;
  className?: string;
  isNotLink?: boolean;
  onPaymentAdded?: () => void;
}

const UnitCardItem: React.FC<UnitCardItemProps> = ({
  listingData,
  IsFooter = true,
  showConfirmation,
  setShowConfirmation,
  handleDeleteUnit,
  isDeletingUnit = false,
  onEditUnit,
  tenantInfo = null,
  className = "",
  isNotLink = false,
  onPaymentAdded,
}) => {
  const [fetchedTenant, setFetchedTenant] = useState<TenantVM | null>(null);
  const [isLoadingTenant, setIsLoadingTenant] = useState(false);
  const [isModalOpen, setModalOpen] = useState(false);
  const {
    handleLeaseInfoClick: openLeaseTooltip,
    handleTenantInfoClick: openTenantTooltip,
    setTooltipState,
    tooltipState,
    handleCloseTooltip,
  } = useLeaseInfoTooltip();

  const navigate = useNavigate();
  const { canWrite } = usePermissions();

  const [paymentModalConfig, setPaymentModalConfig] = useState({
    isOpen: false,
    entityId: null as number | null,
    entityType: EntityType.Property,
  });

  const menuItems = useMemo(() => {
    const items = [];

    if (canWrite("Unit")) {
      items.push({
        icon: editIcon,
        label: "Edit",
        action: () => onEditUnit?.(listingData),
        isDivider: false,
        isDestructive: false,
      });
      items.push({ isDivider: true });
      items.push({
        icon: deleteIcon,
        label: "Delete",
        action: (unitId: string) => setShowConfirmation?.(unitId),
        isDivider: false,
        isDestructive: true,
      });
      items.push({ isDivider: true });
    }

    if (canWrite("Payments")) {
      items.push({
        icon: BluePayableIcon,
        label: "Add Payment",
        action: (unitId: string) => {
          setPaymentModalConfig({
            isOpen: true,
            entityId: Number(unitId),
            entityType: EntityType.Unit,
          });
        },
        isDivider: false,
        isDestructive: false,
      });
      items.push({ isDivider: true });
    }

    if (canWrite("Unit")) {
      items.push({
        icon: notesIcon,
        label: "Add Notes",
        action: () => setModalOpen(true),
        isDivider: false,
        isDestructive: false,
      });
    }

    // Remove any trailing dividers
    while (items.length > 0 && items[items.length - 1].isDivider) {
      items.pop();
    }

    return items;
  }, [setShowConfirmation, onEditUnit, listingData, canWrite]);

  const fetchTenantDetails = async () => {
    if (!listingData?.tenantIds || listingData.tenantIds.length === 0) return;

    setIsLoadingTenant(true);
    try {
      const tenantId = listingData.tenantIds[0]; // assuming single tenant per unit
      const res = await apiHelper(
        TenantService.getApiTenant1,
        { showNotification: false },
        tenantId,
      );

      if (res?.data?.success) {
        setFetchedTenant(res.data.success);
      }
    } catch (err) {
      console.error("Error fetching tenant details:", err);
      setFetchedTenant(null);
    } finally {
      setIsLoadingTenant(false);
    }
  };

  const handleViewMoreClick = async (
    event: React.MouseEvent<HTMLAnchorElement>,
  ) => {
    event.preventDefault();

    const rect = event.currentTarget.getBoundingClientRect();
    const position = {
      top: rect.bottom + window.scrollY + 8,
      left: rect.left + window.scrollX,
    };

    // Step 1: Open tooltip with loading state
    setTooltipState({
      isOpen: true,
      description: (
        <div className="contact-summary-card text-center py-4">
          <div
            className="spinner-border spinner-border-sm text-primary"
            role="status"
          >
            <span className="visually-hidden">Loading...</span>
          </div>
          <span className="ms-2">Loading tenant details...</span>
        </div>
      ),
      position: position,
      title: "Tenant Details",
    });

    // Step 2: If tenant details are already fetched, show them. Otherwise, fetch and then show.
    if (!fetchedTenant && listingData?.tenantIds?.length > 0) {
      setIsLoadingTenant(true);

      try {
        const tenantId = listingData.tenantIds[0];
        const res = await apiHelper(
          TenantService.getApiTenant1,
          { showNotification: false },
          tenantId,
        );

        if (res?.data?.success) {
          setFetchedTenant(res.data.success);
          openTenantTooltip(event, res.data.success, position);
        }
      } catch (err) {
        console.error("Error fetching tenant:", err);
        setTooltipState((prev) => ({
          ...prev,
          description: (
            <div className="text-danger text-center py-3">
              Failed to load tenant details
            </div>
          ),
        }));
      } finally {
        setIsLoadingTenant(false);
      }
    } else if (fetchedTenant) {
      openTenantTooltip(event, fetchedTenant, position);
    }
  };

  const handleLeaseInfoClick = async (
    event: React.MouseEvent<HTMLAnchorElement>,
  ) => {
    event.preventDefault();
    const rect = event.currentTarget.getBoundingClientRect();
    const position = {
      top: rect.bottom + window.scrollY + 8,
      left: rect.left + window.scrollX,
    };

    setTooltipState({
      isOpen: true,
      description: (
        <div className="contact-summary-card text-center py-4">
          <div
            className="spinner-border spinner-border-sm text-primary"
            role="status"
          >
            <span className="visually-hidden">Loading...</span>
          </div>
          <span className="ms-2">Loading lease details...</span>
        </div>
      ),
      position: position,
      title: "Lease Details",
    });

    let tenant: TenantVM | null = fetchedTenant;

    if (!tenant && listingData?.tenantIds?.length > 0) {
      try {
        const tenantId = listingData.tenantIds[0];
        const res = await apiHelper(
          TenantService.getApiTenant1,
          { showNotification: false },
          tenantId,
        );

        if (res?.data?.success) {
          tenant = res.data.success;
          setFetchedTenant(tenant);
          openLeaseTooltip(event, tenant, position);
        }
      } catch (err) {
        console.error("Error fetching tenant for lease info:", err);
        setTooltipState((prev) => ({
          ...prev,
          description: (
            <div className="text-danger text-center py-3">
              Failed to load lease details
            </div>
          ),
        }));
        return;
      }
    } else if (tenant) {
      openLeaseTooltip(event, tenant, position);
    } else {
      setTooltipState((prev) => ({
        ...prev,
        description: <div className="text-center py-3">No active lease</div>,
      }));
    }
  };

  const leaseColor =
    displayColorMap[
      listingData?.leaseDisplayTextColor ?? DisplayColorEnum.Default
    ];

  return (
    <div key={listingData?.id} className={`listing-card ${className}`}>
      {/* Top section */}
      <div
        className="listing-link"
        style={{
          textDecoration: "none",
          color: "inherit",
          cursor: isNotLink ? "auto" : "pointer",
        }}
        onClick={() => {
          if (!isNotLink) {
            navigate(`/units/unitdetails/${listingData?.id}`);
          }
        }}
      >
        <div className="listing-card-header">
          <div className="listing-image-container">
            <img
              src={
                listingData?.mainImageBase64
                  ? `data:image/jpeg;base64,${listingData?.mainImageBase64}`
                  : GetImages.defaultUnitIcon
              }
              alt={`Image of property at ${listingData?.streetAddress}`}
              width={listingData?.mainImageBase64 ? 56 : 20}
              height={listingData?.mainImageBase64 ? 56 : 20}
              className={`listing-image ${
                listingData?.mainImageBase64 ? "" : "listing-image-placeholder"
              }`}
            />
          </div>
          <div className="listing-details">
            <div className="listing-info-container">
              <div className="listing-info">
                <div className="d-flex align-items-center listing-info-center">
                  <h3 className="listing-address">{listingData?.unitNumber}</h3>
                  {!listingData?.tenantIds ||
                  listingData.tenantIds.length === 0 ? (
                    <p className="user-status vacant mx-3 blue-white">Vacant</p>
                  ) : (
                    <></>
                  )}
                </div>
                <p className="listing-type mt-2">
                  {listingData?.streetAddress +
                    " • " +
                    formatUnitEnumLabel(UnitTypesEnum[listingData?.unitTypeId])}
                </p>
              </div>
              <div className="financial-metrics">
                {listingData?.unitTypeId === UnitTypesEnum.Room ? (
                  <>
                    <div className="metric">
                      <p
                        className={`metric-value ${
                          listingData?.propertyId ? "negative" : ""
                        }`}
                      >
                        {listingData?.isBathAttached ? "Attached" : "Common"}
                      </p>
                      <p className="metric-label">Bathroom</p>
                    </div>
                    <div className="separator-vertical" />
                    <div className="metric">
                      <p
                        className={`metric-value ${
                          listingData?.propertyId ? "negative" : ""
                        }`}
                      >
                        <CustomTooltip
                          content={listingData?.formatted?.SizeRaw || "N/A"}
                          disabled={
                            listingData?.formatted?.Size ===
                            listingData?.formatted?.SizeRaw
                          }
                        >
                          <span>{listingData?.formatted?.Size || "N/A"}</span>
                        </CustomTooltip>
                      </p>
                      <p className="metric-label">Sqft</p>
                    </div>
                  </>
                ) : (
                  <>
                    {(listingData?.unitTypeId ===
                      UnitTypesEnum.CondoApartment ||
                      listingData?.formatted?.UnitTypeId ===
                        "Condo/Apartment") && (
                      <>
                        <div className="metric">
                          <p
                            className={`metric-value ${
                              listingData?.propertyId ? "negative" : ""
                            }`}
                          >
                            {listingData?.numberOfBedrooms}
                          </p>
                          <p className="metric-label">Beds</p>
                        </div>
                        <div className="separator-vertical" />
                      </>
                    )}
                    {listingData?.unitTypeId ===
                      UnitTypesEnum.CondoApartment && (
                      <>
                        <div className="metric">
                          <p
                            className={`metric-value ${
                              listingData?.propertyId ? "negative" : ""
                            }`}
                          >
                            {listingData?.numberOfBathrooms}
                          </p>
                          <p className="metric-label">Bath</p>
                        </div>
                        <div className="separator-vertical" />
                      </>
                    )}
                    {(listingData?.unitTypeId === UnitTypesEnum.Studio ||
                      listingData?.unitTypeId ===
                        UnitTypesEnum.CondoApartment) && (
                      <div className="metric">
                        <p
                          className={`metric-value ${
                            listingData?.propertyId ? "negative" : ""
                          }`}
                        >
                          <CustomTooltip
                            content={listingData?.formatted?.SizeRaw || "N/A"}
                            disabled={
                              listingData?.formatted?.Size ===
                              listingData?.formatted?.SizeRaw
                            }
                          >
                            <span>{listingData?.formatted?.Size || "N/A"}</span>
                          </CustomTooltip>
                        </p>
                        <p className="metric-label">Sqft</p>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {IsFooter && (
        <>
          <div className="separator-horizontal" />
          {tenantInfo ? (
            <p className="listing-type unit-card-tenant-info">
              {tenantInfo?.firstName} {tenantInfo?.lastName}
              <span className="contact-dot"> • </span>
              <br className="contact-break" />
              {tenantInfo?.email}
              <span className="contact-dot"> • </span>
              <br className="contact-break" />
              {tenantInfo?.phoneNumber}
            </p>
          ) : (
            <div className="listing-card-footer">
              <div className="listing-stats">
                {Array.isArray(listingData?.tenantIds) &&
                listingData?.tenantIds?.length > 0 ? (
                  <>
                    <div className="stat">
                      <UserIcon className="stat-icon" width="24" height="24" />
                      <span className="stat-text">
                        Tenant:&nbsp;
                        {listingData?.tenantNames?.join(" | ")}
                        {/* --- DOM WRAPPER START --- */}
                        <div className="info-tooltip-wrapper">
                          <a
                            href="#"
                            className="listing-viewmore mx-2"
                            onClick={handleViewMoreClick}
                          >
                            <img
                              src={GetImages.infoBlackIcon}
                              alt="info"
                              className="stat-icon"
                              width={16}
                              height={16}
                            />
                          </a>
                          {tooltipState.isOpen &&
                            tooltipState.title === "Tenant Details" && (
                              <LargeTooltip
                                isOpen={true}
                                onClose={handleCloseTooltip}
                                description={tooltipState.description}
                                position={tooltipState.position}
                                title="Tenant Details"
                                type="tenant"
                              />
                            )}
                        </div>
                        {/* --- DOM WRAPPER END --- */}
                      </span>
                    </div>
                    <div className="stat">
                      <LeaseIcon
                        fill={leaseColor}
                        className="stat-icon remove-icon-filter"
                        width="24"
                        height="24"
                      />

                      <span className="stat-text" style={{ color: leaseColor }}>
                        {listingData?.leaseDisplayText}

                        {/* --- DOM WRAPPER START --- */}
                        <div className="info-tooltip-wrapper">
                          <a
                            href="#"
                            className="listing-viewmore mx-2"
                            onClick={handleLeaseInfoClick}
                          >
                            <img
                              src={GetImages.infoBlackIcon}
                              alt="info"
                              className="stat-icon"
                              width={16}
                              height={16}
                            />
                          </a>
                          {tooltipState.isOpen &&
                            tooltipState.title === "Lease Details" && (
                              <LargeTooltip
                                isOpen={true}
                                onClose={handleCloseTooltip}
                                description={tooltipState.description}
                                position={tooltipState.position}
                                title="Lease Details"
                                type="tenant"
                              />
                            )}
                        </div>
                        {/* --- DOM WRAPPER END --- */}
                      </span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="stat">
                      <VacantIcon
                        fill="var(--text-secondary)"
                        className="stat-icon"
                        width="24"
                        height="24"
                      />
                      <span className="stat-text">
                        Vacant: {"Since " + 0 + " Months"}
                      </span>
                    </div>
                    <div className="stat">
                      <UserIcon
                        fill="var(--grey-subtext1)"
                        className="stat-icon"
                        width="28"
                        height="28"
                      />
                      <span className="stat-text">Prospect: {0}</span>
                    </div>
                  </>
                )}
              </div>
              <div className="footer-actions">
                <div className="footer-highlights">
                  {listingData?.expensesDue > 0 && (
                    <div className="highlight-value negative">
                      <ArrowOutgoingIcon
                        fill="var(--color-danger)"
                        className="highlight-icon"
                      />
                      <CurrencyTooltip
                        content={listingData?.formatted?.ExpensesDueRaw}
                        formatted={listingData?.formatted?.ExpensesDue}
                      >
                        <span className="highlight-text mx-2">
                          {listingData?.formatted?.ExpensesDue}
                        </span>
                        {/*  */}
                      </CurrencyTooltip>
                    </div>
                  )}
                  {listingData?.formatted?.IncomeDue != 0 && (
                    <div className="highlight-value positive">
                      <ArrowIncomingIcon
                        fill="var(--color-success)"
                        className="highlight-icon"
                      />
                      <CurrencyTooltip
                        content={listingData?.formatted?.IncomeDueRaw}
                        formatted={listingData?.formatted?.IncomeDue}
                      >
                        <span className="highlight-text mx-2">
                          {listingData?.formatted?.IncomeDue}
                        </span>
                      </CurrencyTooltip>
                    </div>
                  )}
                </div>
                {menuItems.length > 0 && (
                  <ActionMenu
                    menuItems={menuItems?.map((item) => ({
                      ...item,
                      action: () => item.action(String(listingData.id)),
                    }))}
                    trigger={
                      <button
                        className="action-button"
                        aria-label="More options"
                      >
                        <img
                          src={dropdownIcon}
                          alt="More options"
                          width={20}
                          height={20}
                          className="action-icon"
                        />
                      </button>
                    }
                  />
                )}
              </div>
            </div>
          )}
        </>
      )}

      {isModalOpen && (
        <AddNotesForm
          entityId={listingData?.id}
          entityTypeId={NotesEntityTypesEnum.Unit}
          isModalOpen={isModalOpen}
          setModalOpen={setModalOpen}
        />
      )}

      {/* Add PAYMENT*/}
      {paymentModalConfig.isOpen && (
        <AddPaymentModal
          isOpen={paymentModalConfig.isOpen}
          onClose={() => {
            setPaymentModalConfig((prev) => ({ ...prev, isOpen: false }));
            // Trigger Refresh
            if (onPaymentAdded) onPaymentAdded();
          }}
          entityIdProp={paymentModalConfig.entityId || undefined}
          entityTypeIdProp={paymentModalConfig.entityType}
          defaultPaymentMode="1"
        />
      )}

      {/* Confirmation Popup for Delete */}
      {showConfirmation === String(listingData?.id) && (
        <ConfirmationPopup
          isOpen={true}
          title="Delete Unit"
          message="Do you want to delete this unit?"
          primaryButtonLabel="Delete"
          secondaryButtonLabel="Cancel"
          onPrimaryAction={() => handleDeleteUnit?.(listingData?.id)}
          onSecondaryAction={() => setShowConfirmation?.(null)}
          onClose={() => setShowConfirmation?.(null)}
          isPrimaryDestructive={true}
          isLoading={isDeletingUnit}
          loadingLabel="Deleting..."
        />
      )}
    </div>
  );
};

export default UnitCardItem;
